import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { Instructor } from "../../providers/instructorsProvider";
import { InstructorOrEmployeeGrantsInput } from "../../inputs/InstructorOrEmployeeGrantsInput";
import { Column } from "../../misc/Column";
import { FormCard } from "../../misc/FormCard";
import { UserAvatarForm } from "../../misc/UserAvatarForm";
import { UserRolesForm } from "../../misc/UserRolesForm";
import {
  BooleanInput,
  CheckboxGroupInput,
  SelectArrayInput,
  TextInput,
  useDataProvider,
  useGetList,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { schemas } from "../../generated/serverClient";
import { grants } from "../../backoffice.access_control";
import { DrivingSchoolUserRolesForm } from "../drivingSchool/DrivingSchoolUserRolesForm";
import { useState } from "react";
import { DateTime } from "luxon";
import { LoadingButton } from "@mui/lab";
import { Button } from "@mui/material";
import { cardTitleStyle, overviewCardStyle } from "../../misc/backofficeTheme";
import { ResetPasswordSection } from "../../misc/ResetPasswordSection";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";

export function InstructorAdminForm() {
  const isAutovioAdmin = grants.has("admin");
  const canManageUsers = isAutovioAdmin || grants.has("manageUsers");

  return (
    <div data-testid="InstructorAdminForm">
      <Grid container spacing="30px">
        <Grid item xs={12} sm={6}>
          <Column gap="30px">
            <_InstructorDetailsEditForm />
            {grants.has("Instructor:assignBranches") && <InstructorBranchesFormCard />}
            {canManageUsers && <InstructorStatusSection />}
            {isAutovioAdmin && <UserAvatarForm<Instructor> />}
            <InstructorResetPasswordSection />
            {isAutovioAdmin ? (
              <UserRolesForm resource="autovioEmployees" />
            ) : canManageUsers ? (
              <DrivingSchoolUserRolesForm />
            ) : null}
          </Column>
        </Grid>
        {canManageUsers && (
          <Grid item xs={12} sm={6}>
            <Column gap="30px">
              <FormCard title="Berechtigungen">
                <InstructorOrEmployeeGrantsInput scope="drivingSchoolManagerAndAutovioAdmin" />
              </FormCard>
              {isAutovioAdmin && (
                <FormCard title="Berechtigungen (Nur AUTOVIO)">
                  <InstructorOrEmployeeGrantsInput scope="autovioAdminOnly" />
                  <BooleanInput label="Zugriff auf das AUTOVIO Lab in der App" source="featureToggles.lab" />
                </FormCard>
              )}
            </Column>
          </Grid>
        )}
      </Grid>
    </div>
  );
}

function InstructorStatusSection() {
  const instructor = useRecordContext<Instructor>();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!instructor) {
    return null;
  }

  const shouldDeactivate = !!instructor.isActive;

  async function handleConfirm() {
    setLoading(true);
    const targetEndDate = shouldDeactivate ? DateTime.now().startOf("day") : DateTime.fromISO("2050-01-01");
    try {
      await runSafely(
        () =>
          dataProvider.update("instructors", {
            id: instructor.id,
            data: { endDate: targetEndDate },
            previousData: instructor,
          }),
        {
          successMessage: shouldDeactivate
            ? `${instructor.name} wurde erfolgreich deaktiviert.`
            : `${instructor.name} wurde erfolgreich aktiviert.`,
          errorMessage: shouldDeactivate
            ? `Fehler beim Deaktivieren von ${instructor.name}.`
            : `Fehler beim Aktivieren von ${instructor.name}.`,
          onSuccess: () => {
            setDialogOpen(false);
            refresh();
          },
        },
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={overviewCardStyle}>
      <Typography sx={cardTitleStyle}>Account</Typography>
      <Typography sx={{ mb: 2 }}>
        Status: <strong>{instructor.isActive ? "Aktiv" : "Inaktiv"}</strong>
      </Typography>
      <Button
        variant="outlined"
        color={instructor.isActive ? "warning" : "success"}
        onClick={() => setDialogOpen(true)}
      >
        {instructor.isActive ? "Deaktivieren" : "Aktivieren"}
      </Button>
      <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)}>
        <DialogTitle>{instructor.isActive ? "Fahrlehrer deaktivieren" : "Fahrlehrer aktivieren"}</DialogTitle>
        <DialogContent sx={{ pt: 2, minWidth: 400 }}>
          <Typography>
            {instructor.isActive
              ? `Soll ${instructor.name} wirklich deaktiviert werden?`
              : `Soll ${instructor.name} wirklich aktiviert werden?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <LoadingButton variant="outlined" onClick={() => setDialogOpen(false)} disabled={loading}>
            Abbrechen
          </LoadingButton>
          <LoadingButton variant="contained" onClick={handleConfirm} loading={loading}>
            {instructor.isActive ? "Deaktivieren" : "Aktivieren"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function _InstructorDetailsEditForm() {
  return (
    <FormCard title="Persönliche Daten">
      <Column>
        <TextInput label="Vorname" source="firstName" fullWidth />
        <TextInput label="Nachname" source="lastName" fullWidth />
        <TextInput label="Telefonnummer" source="phoneNumber" fullWidth />
        <SelectArrayInput
          label="Führerscheinklassen"
          source="instructing.drivingLicenseClasses"
          choices={_licenseClassesChoices}
          fullWidth
        />
      </Column>
    </FormCard>
  );
}

function InstructorBranchesFormCard() {
  const { id } = useCurrentDrivingSchool();
  const { data: activeBranches = [] } = useGetList("branches", {
    filter: { drivingSchoolId: id, isActive: true },
    pagination: { page: 1, perPage: 1000 },
  });

  if (activeBranches.length <= 1) {
    return null;
  }

  return (
    <FormCard title="Filialen">
      <CheckboxGroupInput
        source="assignedBranches"
        label={<></>}
        choices={[...activeBranches]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((branch) => ({ id: branch.id, name: branch.name }))}
        row={false}
      />
    </FormCard>
  );
}

function InstructorResetPasswordSection() {
  const instructor = useRecordContext<Instructor>();
  if (!instructor) {
    return null;
  }

  return (
    <ResetPasswordSection
      name={instructor.name}
      email={instructor.email}
      noEmailErrorMessage="Für diesen Fahrlehrer ist keine E-Mail-Adresse hinterlegt."
    />
  );
}

const _licenseClassesChoices = schemas.DrivingLicenseClassEnum.options.map((it) => ({ id: it, name: it }));
